import { useEffect, useState, useCallback } from 'preact/hooks'
import { RANGE_INDICATOR } from 'libs/searchspring/components/collection/filters/FilterUtils'
import debounce from 'lodash.debounce'
import { formatValue } from 'libs/searchspring/components/collection/filters/filterHooks'

export function FilterInputSlider({ filter, onFilter, fetchedFilter }) {
  const minimum = Math.min(filter.range[0], fetchedFilter?.range[0])
  const maximum = Math.max(filter.range[1], fetchedFilter?.range[1])

  // Pass along the facet_active status to the RangeSlider
  const handleChange = (value) => {
    onFilter(`${filter.field}:${value.min}${RANGE_INDICATOR}${value.max}`)
  }

  return (
      <div class="flex flex-col gap-2 w-full px-10 pb-4">
        <RangeSlider
          min={minimum}
          max={maximum}
          step={filter.step}
          format={filter.format}
          onChange={handleChange}
          isActive={fetchedFilter?.facet_active > 0}
        />
      </div>
  )
}

const RangeSlider = ({ min, max, step, onChange, format, isActive }) => {
  const [minValue, setMinValue] = useState(min)
  const [maxValue, setMaxValue] = useState(max)

  // Reset the slider positions when not active
  useEffect(() => {
    if (!isActive) {
      setMinValue(min)
      setMaxValue(max)
    }
  }, [isActive, min, max])

  // Create a debounced version of onChange that only fires after 300ms of inactivity
  const debouncedOnChange = useCallback(
    debounce((values) => {
      onChange(values)
    }, 300),
    [onChange]
  )

  const handleMinChange = (event) => {
    const newMinVal = Math.min(+event.target.value, maxValue - step)
    setMinValue(newMinVal)

    // Call the debounced function instead of onChange directly
    debouncedOnChange({ min: newMinVal, max: maxValue })
  }

  const handleMaxChange = (event) => {
    const newMaxVal = Math.max(+event.target.value, minValue + step)
    setMaxValue(newMaxVal)

    // Call the debounced function instead of onChange directly
    debouncedOnChange({ min: minValue, max: newMaxVal })
  }

  // Cancel the debounced function when component unmounts
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  const minPos = ((minValue - min) / (max - min)) * 100
  const maxPos = ((maxValue - min) / (max - min)) * 100
  const thumbStyle = 'absolute top-1/2 -translate-y-1/2 z-[2] h-[--thumb-size] w-[--thumb-size] rounded-full bg-brand-black -translate-x-1/2'
  const labelStyle = 'absolute top-full -translate-x-1/2 pointer-events-none paragraph-sm'
  return (
        <div className="relative flex items-center h-[--thumb-size] mx-[calc(var(--thumb-size)_/_-2)]"
            style={{ '--thumb-size': '1.5rem' }}
        >
          <div className="absolute h-[--thumb-size] w-[calc(100%_+_var(--thumb-size))] mx-[calc(var(--thumb-size)_/_-2)]">
            <input
              className="range pointer-events-none absolute z-[3] h-full w-full appearance-none p-0 opacity-0"
              type="range"
              value={minValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMinChange}
            />
            <input
              className="range pointer-events-none absolute z-[3] h-full w-full appearance-none p-0 opacity-0"
              type="range"
              value={maxValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMaxChange}
            />
          </div>
          <div className="absolute h-[--thumb-size] w-full">
            <div
              className={thumbStyle}
              style={{ left: `${minPos}%` }}
            />
            <div className={labelStyle}
              style={{ left: `${minPos}%` }}
            >
              {format ? formatValue(minValue, format) : minValue}
            </div>
            <div className="absolute z-0 w-full h-1 -translate-y-1/2 rounded-full top-1/2 bg-neutral-medium">
              <div className="absolute h-full bg-brand-pink" style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }} />
            </div>
            <div
              className={thumbStyle}
              style={{ left: `${maxPos}%` }}
            />
            <div className={labelStyle}
              style={{ left: `${maxPos}%` }}
            >
              {format ? formatValue(maxValue, format) : maxValue}
              </div>
          </div>
        </div>
  )
}
